import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { ApiModels, HttpService } from '@mona/api';
import { Logger } from '@mona/shared/logger';
import { AppError, compactObject } from '@mona/shared/utils';
import { ApiTokenResponse, hasAdminRole, TokenResponse, User } from '../models';

/**
 * API abstraction layer for the User authentication API
 */
@Injectable({ providedIn: 'root' })
export class AuthApi {
    private logger = new Logger('AUTH');

    /**
     * Constructor
     *
     * @param http HttpClient
     */
    constructor(private http: HttpService) {}

    /**
     * Registers an rfid for a user
     *
     * @param rfid id string
     * @param pin pin code
     */
    registerRfiD(rfid: string, pin: string): Observable<User> {
        return this.http
            .post<ApiModels.Practitioner>(`/pdms/practitioners/register-rfid/`, {
                rfid,
                pin,
            } as ApiModels.PractitionerRegistrationRequest)
            .pipe(map(apiUser => new User(apiUser)));
    }

    /**
     * Update an rfid for a user
     *
     * @param rfid id string
     */
    updateRfiD(rfid: string): Observable<unknown> {
        return this.http.post<any>(`/pdms/practitioners/update-rfid/`, {
            rfid,
        });
    }

    /**
     * Discards an rfid for a user
     *
     * @param rfid id string
     */
    discardRfiD(rfid: string): Observable<unknown> {
        return this.http.post<ApiModels.Practitioner>(`/pdms/practitioners/remove-rfid/`, {
            rfid,
        });
    }

    /**
     * Authenticate using the rfid
     *
     * @param rfid Serial
     */
    verifyRfId(rfid: string): Observable<any> {
        const rfidVerified = new RegExp(`^[ -~]*$`).test(rfid) && rfid.length > 4;

        if (!rfidVerified) {
            return throwError('Wrong RFID');
        }

        return this.http.post<ApiModels.Practitioner>(`/pdms/practitioners/verify-rfid/`, { rfid }).pipe(
            map(apiUser => {
                const user = new User(apiUser);
                return {
                    rfid,
                    user,
                    isAdmin: hasAdminRole(user),
                };
            }),
        );
    }

    /**
     * Performs a request with user credentials
     * in order to get auth tokens
     *
     * @param {string} username
     * @param {string} password
     * @param {string} device_id
     * @returns Observable<AccessData>
     */
    loginWithCreds(username: string, password: string, device_id?: string): Observable<TokenResponse> {
        const payload = compactObject({
            username,
            password,
            device_id,
        });
        return this.http
            .post<ApiTokenResponse>(`/auth/user/login`, payload)
            .pipe(map(res => TokenResponse.toModel(res)));
    }

    /**
     * Performs a request with user RFID
     * in order to get auth tokens
     *
     * @param {string} rfid
     * @param {string} device_id
     * @returns Observable<AccessData>
     */
    loginWithRfid(rfid: string, device_id?: string): Observable<TokenResponse> {
        const payload = compactObject({
            rfid,
            device_id,
        });
        return this.http
            .post<ApiTokenResponse>(`/auth/user/rfid`, payload)
            .pipe(map(res => TokenResponse.toModel(res)));
    }

    /**
     * Asks for a new access token given
     * the stored refresh token
     *
     * @param refreshToken
     * @param rfid
     * @param device_id
     * @returns {Observable<TokenResponse>}
     */
    refreshToken(refreshToken: string, rfid: string, device_id?: string): Observable<TokenResponse> {
        if (!refreshToken) {
            return throwError(() => new Error('Refresh token does not exist'));
        }
        const payload = compactObject({
            rfid,
            device_id,
            refresh_token: refreshToken,
        });

        return this.http.post<ApiTokenResponse>(`/auth/user/refresh/`, payload).pipe(
            map(res => TokenResponse.toModel(res)),
            catchError(error => {
                this.logger.error(`'/auth/user/refresh/' request failed`, error);
                return throwError(error);
            }),
            finalize(() => {
                this.logger.debug(`'/auth/user/refresh/' request completed `);
            }),
        );
    }

    /**
     * Returns user based on id
     *
     * @param id
     * @returns {Observable<User>}
     */
    getAuthUser(id: string): Observable<User> {
        return this.http
            .get<ApiModels.Practitioner>(`/pdms/practitioners/${id}`)
            .pipe(map(practitioner => new User(practitioner)));
    }

    /**
     * Get practitioners permissions
     *
     */
    getPermissions(): Observable<string[]> {
        return this.http.post<{ name: string }[]>(`/pdms/practitioners/permissions/`, {}).pipe(
            map(res => res.map(({ name }) => name).sort()),
            catchError(error => {
                throw new AppError(error);
            }),
        );
    }

    /**
     * Send logout request
     *
     * @returns {Observable<any>}
     */
    logout(): Observable<any> {
        return this.http.post<ApiTokenResponse>(`/auth/user/logout`, null).pipe(
            catchError(error => {
                throw new AppError(error);
            }),
        );
    }

    /**
     * Send change password request
     *
     * @param {string} old_password - The current password of the practitioner.
     * @param {string} new_password - The new password to set for the practitioner.
     * @param {string} username - The username of the practitioner whose password is being changed.
     * @returns {Observable<ApiTokenResponse>} An observable that emits the API token response.
     * @throws {AppError} If the request to change the password fails.
     */
    changePassword(old_password: string, new_password: string, username: string) {
        return this.http
            .post<ApiTokenResponse>(`/pdms/practitioners/change-password`, {
                old_password,
                new_password,
                username,
            })
            .pipe(
                catchError(error => {
                    throw new AppError(error);
                }),
            );
    }
}
